import React, { useEffect, useState, useMemo } from 'react'
import DataTable from 'react-data-table-component'
import { useAdminStoreActions, useAdminStoreData } from '../../store/adminStore'
import { Badge, Card } from 'reactstrap'
import { useTranslation } from 'react-i18next'
import Icon from '../../components/icon/Icon'

const AdminActivities = () => {
  const { fetchActivities, setPage, setSize, setSort, setFilters } = useAdminStoreActions()
  const activities = useAdminStoreData.activities()
  const loading = useAdminStoreData.loading()
  const totalElements = useAdminStoreData.totalElements()
  const page = useAdminStoreData.page()
  const size = useAdminStoreData.size()

  const [filterText, setFilterText] = useState('')

  const { t } = useTranslation()

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters({ userName: filterText })
    }, 1000)

    return () => clearTimeout(timer)
  }, [filterText, setFilters])

  useEffect(() => {
    fetchActivities()
  }, [])

  const columns = useMemo(() => [
    {
      name: t('admin.table.status'),
      selector: row => row.success,
      sortable: true,
      sortField: 'success',
      width: '100px',
      cell: row => (
        <Badge color={row.success ? 'success' : 'danger'} pill>
          {row.success ? t('admin.table.success') : t('admin.table.failed')}
        </Badge>
      )
    },
    {
      name: t('admin.table.user'),
      selector: row => row.userName,
      sortable: true,
      sortField: 'u.userName',
      wrap: true
    },
    {
      name: t('admin.table.endpoint'),
      selector: row => row.endpoint,
      sortable: true,
      sortField: 'endpoint',
      wrap: true
    },
    {
      name: t('admin.table.method'),
      selector: row => row.method,
      sortable: true,
      sortField: 'method',
      width: '100px'
    },
    {
      name: t('admin.table.responseTime'),
      selector: row => row.responseTime,
      sortable: true,
      sortField: 'responseTime',
      right: true,
      width: '150px'
    },
    {
      name: t('admin.table.ip'),
      selector: row => row.ip,
      sortable: true,
      sortField: 'ip',
      width: '150px'
    },
    {
      name: t('admin.table.request'),
      selector: row => row.request,
      wrap: true,
      minWidth: '200px',
      cell: row => <div className="text-muted small" style={{ maxHeight: '60px', overflowY: 'auto' }}>{row.request}</div>
    },
    {
      name: t('admin.table.error'),
      selector: row => row.error,
      wrap: true,
      cell: row => row.error && <div className="text-danger small">{row.error}</div>
    }
  ], [t])

  const handlePageChange = page => {
    setPage(page - 1)
  }

  const handlePerRowsChange = (newPerPage, page) => {
    setSize(newPerPage)
  }

  const handleSort = (column, sortDirection) => {
    if (column.sortField) {
      setSort(`${column.sortField},${sortDirection}`)
    }
  }

  const paginationComponentOptions = {
    rowsPerPageText: t('admin.pagination.rowsPerPage'),
    rangeSeparatorText: t('admin.pagination.rangeSeparator'),
    selectAllRowsItem: false,
    selectAllRowsItemText: 'All'
  }

  return (
    <div className="admin-activities">
      <Card className="card-bordered card-preview">
        <div className="card-inner position-relative text-end">
          <div className="card-title-group">
            <div className="card-tools">
              <div className="form-inline flex-nowrap gx-3">
                <div className="form-control-wrap w-200">
                  <div className="form-icon form-icon-left">
                    <Icon name="search"/>
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    id="default-04"
                    placeholder={t('admin.searchPlaceholder')}
                    onChange={(e) => setFilterText(e.target.value)}
                    style={{ width: '20rem' }}
                  />
                </div>
              </div>
            </div>
          </div>
          <DataTable
            columns={columns}
            data={activities}
            progressPending={loading}
            pagination
            paginationServer
            paginationTotalRows={totalElements}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            paginationDefaultPage={page + 1}
            paginationPerPage={size}
            paginationComponentOptions={paginationComponentOptions}
            sortServer
            onSort={handleSort}
            highlightOnHover
            pointerOnHover
            noDataComponent={<div className="p-2">{t('admin.table.noData')}</div>}
          />
        </div>
      </Card>
    </div>
  )
}

export default AdminActivities
